import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useComponentVisible from '../hooks/useComponentVisible';
import getName from "../util/getName";
import { AppContext } from "../util/AppContext";
import { getConfigValue, isBatchPrintEnabledForAnyProduct, isProfessionalPrintingEnabledForProduct } from "../util/getConfigValue";
import { focusTypeName } from "../util/constants";
import { useRoutingValidation } from "../util/useRoutingValidation";
import PreviewModal from "../components/PreviewModal";
import { useBreadcrumbs } from "../util/BreadcrumbContext";
import CreateSalesAidStepProgressBar, { SalesAidStepIds } from "../components/CreateSalesAidStepProgressBar";

function GenerateDocumentPage() {
    const [appState, setAppState] = useContext(AppContext);
    const [buttonRef, isDropdownVisible, setIsDropdownVisible] = useComponentVisible(false);
    const canDownload = getConfigValue(appState.config?.Generate?.Document?.Download?.Enabled, appState)?.Value === 'true' || false;
    const canPrint = getConfigValue(appState.config?.Generate?.Document?.Print?.Enabled, appState)?.Value === 'true' || false;
    const professionalPrintingEnabled = isProfessionalPrintingEnabledForProduct(appState, appState.currentProduct?.clientProductId);
    const batchPrintEnabled = isBatchPrintEnabledForAnyProduct(appState);
    const [showPreviewModal, setShowPreviewModal] = useState(true);
    const hcpOpportunityEnabled = getConfigValue(appState.config?.HCP?.Opportunity?.Enabled, appState)?.Value === "true";
    const navigate = useNavigate();
    const { state } = useLocation();

    const [name] = useState(getName(appState));
    const [territory] = useState(appState.territory?.name || "");
    const focusType = (appState?.focus);
    const {setBreadcrumbs } = useBreadcrumbs();

    //territory selection can be skipped if either the user has only one territory (bypassTerritorySelection) 
    // or if the focus is HCPs and the HCP Opportunity feature is enabled. 
    // In both cases, the user can directly go to the HCP page instead of first going to territory selection.
    const skipTerritorySelection = appState?.bypassTerritorySelection || (focusType === focusTypeName.HCPs && hcpOpportunityEnabled);

	useEffect(() => {
		setBreadcrumbs([
			{ label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/FocusPage"}		
		]);
	}, [setBreadcrumbs]);

    useRoutingValidation();

    function handleClick() {
        if (appState?.bypassTerritorySelection) {
            startNewSalesAid(true);
        } else {
            setIsDropdownVisible(state => !state);
        }
    }

    function startNewSalesAid(startNew = true) {
		// Keep territory and market if not starting new.
		// If the user only has one territory (bypassTerritorySelection), then also keep them.
		const keepTerritoryAndMarket = !startNew || appState?.bypassTerritorySelection;
		setAppState({
			...appState,
			...(!keepTerritoryAndMarket && {
				market: {},
				territory: {},
			}),
			prescriber: {},
			products: [],
			currentProduct: '',
			plans: [],
			category: '',
			mainMessage: '',
            purchaseOrderNumber: "",
		});
		if (focusType === focusTypeName.Messages) {
			navigateNewSalesAid(startNew, '/FocusPage', '/MessageSelectionPage');
		} else if (focusType === focusTypeName.HCPs) {
			if (batchPrintEnabled) {
				navigateNewSalesAid(startNew, '/FocusPage', '/HCPPage');
			} else {
				navigateNewSalesAid(startNew && !skipTerritorySelection, '/TerritoryPage', '/HCPPage');
			}
		}
	}

    function navigateNewSalesAid(startNew, newPath, regularPath) {
        if (startNew) {
            navigate(newPath);
        } else {
            navigate(regularPath);
        }
    }

    return (
        <>
            <CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.COMPLETED} focusType={appState?.focus} />

            <div className="container-xl mt-5">
                <div className="row">
                    <div className="col-12">


                        {(canPrint || canDownload || professionalPrintingEnabled) &&
                            <PreviewModal
                                show={showPreviewModal}
                                setShow={setShowPreviewModal}
                                pdfUrl={state?.pdfURL}
                                isLoading={false}
                                showGenerateBack={false}
                                showPrintDownload={true}
                                onGenerateClick={null}
                                isValid={true}
                                pdfFileName={state?.fileName}
                                supportsProfessionalPrinting={professionalPrintingEnabled}
                                provider={name}
                                territory={territory}
                                asModal={false}
                                returnURL={'/GenerateDocumentPage'}
                            />
                        }
                    </div>
                </div>
                <div className="row justify-content-end mt-6 mb-7">
                    <div className="col-10 col-sm-8 col-md-6 col-lg-8 col-xl-7 offset-xl-1 col-xxl-6 offset-xxl-2 justify-content-end">
                        <div className="d-flex flex-column flex-lg-row-reverse">
                            <div className="button-wrapper w-100">
                                <div className="dropdown dropup d-flex">
                                    <button
                                        onClick={handleClick}
                                        ref={buttonRef}
                                        className="btn btn-primary dropdown-toggle btn-flex pe-7 mb-3 mb-lg-0 ms-lg-3 pe-lg-3 flex-fill"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        {getConfigValue(appState.config?.GenerateDocumentPage?.NewButton, appState)?.Value}
                                    </button>
                                    {!appState?.bypassTerritorySelection &&
                                        <ul
                                            className="dropdown-menu dropdown-menu-end"
                                            style={{
                                                display: isDropdownVisible ? "block" : "none",
                                                position: "absolute",
                                                bottom: "48px",
                                            }}
                                        >
                                            <li className="mb-2">
                                                <span className="dropdown-item-text type-bold">
                                                    {getConfigValue(appState.config?.GenerateDocumentPage?.NewButton?.Question, appState)?.Value}
                                                </span>
                                            </li>
                                            <li>
                                                <hr className="dropdown-divider" />
                                            </li>
                                            <li>
                                                <button type="button" to="#" className="dropdown-item" onClick={() => startNewSalesAid(false)}>
                                                    {getConfigValue(appState.config?.GenerateDocumentPage?.NewButton?.KeepTerritory, appState)?.Value}
                                                </button>
                                            </li>
                                            <li>
                                                <button type="button" to="#" className="dropdown-item" onClick={() => startNewSalesAid(true)}>
                                                    {getConfigValue(appState.config?.GenerateDocumentPage?.NewButton?.BrandNew, appState)?.Value}
                                                </button>
                                            </li>
                                        </ul>
                                    }
                                </div>
                            </div>
                            <div className="button-wrapper flex-fill w-100">
                                <Link to="/RecentSalesAidsPage">
                                    <button className="btn btn-secondary btn-flex pe-7 pe-lg-3 flex-fill w-100">
                                        {getConfigValue(appState.config?.GenerateDocumentPage?.DashboardButton, appState)?.Value}
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
}

export default GenerateDocumentPage;